import { useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, MailCheck } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-mark.png" alt="Vixelforge" className="w-8 h-8 shrink-0" />
            <span className="font-black text-xl tracking-tight">Vixelforge</span>
          </Link>
          <LanguageSwitcher />
        </div>

        {sent ? (
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-8 text-center" data-testid="forgot-sent">
            <MailCheck className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-black text-2xl mb-2">{t("check_your_inbox") || "Check your inbox"}</h1>
            <p className="text-zinc-400 text-sm">
              {t("forgot_sent_msg") || "If an account exists for that email, we've sent a link to reset your password. The link expires in 60 minutes."}
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 mt-6 text-sm text-orange-400 hover:text-orange-300">
              <ArrowLeft className="w-4 h-4" /> {t("back_to_login") || "Back to sign in"}
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-black text-3xl md:text-4xl tracking-tight mb-2">
              {t("forgot_password") || "Forgot your password?"}
            </h1>
            <p className="text-zinc-400 text-sm mb-6">
              {t("forgot_password_hint") || "Enter your email and we'll send you a link to set a new password."}
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("email")}</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="bg-[#16161D] border-white/10 focus:ring-orange-500 mt-1.5 h-11" />
              </div>
              <Button type="submit" disabled={loading || !email} data-testid="forgot-submit-btn"
                className="w-full h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
                {loading ? "..." : (t("send_reset_link") || "Send reset link")}
              </Button>
            </form>
            <p className="text-sm text-center mt-6">
              <Link to="/login" className="text-zinc-400 hover:text-orange-300 inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> {t("back_to_login") || "Back to sign in"}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
